import React, { memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { Input } from 'ui-components/components/input';
import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';
import { emptyObject } from 'ui-components/default-props/empty';
import { SimpleDropdown } from '../../components/simple-dropdown';
import { InputWithAutocomplete } from '../input-with-autocomplete/input-with-autocomplete';
import { messages } from './messages';
import styles from './styles.module.less';
import { amountUnits } from './amount-units';

const packagingGroupItems = [
  {
    key: 'i',
    name: 'i',
  },
  {
    key: 'ii',
    name: 'ii',
  },
  {
    key: 'iii',
    name: 'iii',
  },
];

/**
 * @typedef {object} DangerousGoodsFormType
 * @property {string} [idNumber]
 * @property {string} [shippingName]
 * @property {string} [technicalName]
 * @property {string} [productClass]
 * @property {string} [productClassSubsidiary]
 * @property {string} [packagingType]
 * @property {number} [quantity]
 * @property {{ amount: number, unit: 'kg' | 'g' | "l" | "ml"}} [dangerousAmount]
 * @property {'i' | 'ii' | 'iii'} [packagingGroup]
 */

/**
 * Component for displaying and editing dangerous goods for product
 * @param {object} props
 * @param {(arg: DangerousGoodsFormType) => void} props.onChange
 * @param {DangerousGoodsFormType} props.value
 * @returns {JSX.Element}
 */
const DangerousGoodsFormComponent = ({ onChange, value = emptyObject }) => {
  const updateValueByProperty = useCallback(
    (property) => {
      return (newValue) => {
        onChange({
          ...value,
          [property]: newValue?.target?.value,
        });
      };
    },
    [onChange, value],
  );

  return (
    <div className={styles['grid-container']}>
      <div>
        <Text htmlFor='idNumber' textType={TextType.Label} value={messages.unNumber} />
        <Input id='idNumber' onChange={updateValueByProperty('idNumber')} value={value.idNumber ?? ''} version='version2' />
      </div>
      <div>
        <Text htmlFor='shippingName' textType={TextType.Label} value={messages.shippingName} />
        <Input id='shippingName' onChange={updateValueByProperty('shippingName')} value={value.shippingName ?? ''} version='version2' />
      </div>
      <div>
        <Text htmlFor='technicalName' textType={TextType.Label} value={messages.technicalName} />
        <Input id='technicalName' onChange={updateValueByProperty('technicalName')} value={value.technicalName ?? ''} version='version2' />
      </div>
      <div>
        <Text htmlFor='productClass' textType={TextType.Label} value={messages.productClass} />
        <Input id='productClass' onChange={updateValueByProperty('productClass')} value={value.productClass ?? ''} version='version2' />
      </div>
      <div>
        <Text htmlFor='productClassSubsidiary' textType={TextType.Label} value={messages.subsidiaryRisk} />
        <Input
          id='productClassSubsidiary'
          onChange={updateValueByProperty('productClassSubsidiary')}
          value={value.productClassSubsidiary ?? ''}
          version='version2'
        />
      </div>
      <div className={styles['packaging-group']}>
        <Text htmlFor='packagingGroup' textType={TextType.Label} value={messages.packagingGroup} />
        <SimpleDropdown
          defaultTitle={<FormattedMessage {...messages.selectAnOption} />}
          dropdownMenuShouldHaveWidthOfButton
          items={packagingGroupItems}
          onItemSelected={(packagingGroup) => {
            onChange({
              ...value,
              packagingGroup,
            });
          }}
          selectedItemKey={value.packagingGroup}
        />
      </div>
      <div>
        <Text htmlFor='quantity' textType={TextType.Label} value={messages.quantity} />
        <NumberInput
          id='quantity'
          min={0}
          onChange={(quantity) => {
            onChange({
              ...value,
              quantity,
            });
          }}
          value={value.quantity ?? 0}
        />
      </div>
      <div>
        <Text element='span' textType={TextType.Label} value={messages.amount} />
        <div className={styles['dangerous-goods-amount']}>
          <NumberInput
            id='amount'
            min={0}
            onChange={(amount) => {
              onChange({
                ...value,
                dangerousAmount: {
                  amount,
                  unit: value.dangerousAmount?.unit,
                },
              });
            }}
            value={value.dangerousAmount?.amount ?? 0}
          />
          <div className={styles['amount-unit']}>
            <InputWithAutocomplete
              items={amountUnits}
              onChange={(unit) => {
                onChange({
                  ...value,
                  dangerousAmount: {
                    unit,
                    amount: value.dangerousAmount?.amount,
                  },
                });
              }}
              placeholder={messages.unit}
              value={value.dangerousAmount?.unit ?? ''}
            />
          </div>
        </div>
      </div>
      <div>
        <Text htmlFor='packagingType' textType={TextType.Label} value={messages.packagingType} />
        <Input id='packagingType' onChange={updateValueByProperty('packagingType')} value={value.packagingType ?? ''} version='version2' />
      </div>
    </div>
  );
};

export const DangerousGoodsForm = memo(DangerousGoodsFormComponent);
